import React from 'react';

const MeetingSummaryModal = ({ meeting, onClose }) => {
  if (!meeting) return null;

  const calculateConversionRate = () => {
    if (meeting.contactsGenerated === 0) return '0%';
    const rate = (meeting.patients / meeting.contactsGenerated) * 100;
    return `${rate.toFixed(1)}%`;
  };

  // Función para formatear fechas correctamente en zona horaria de México
  const formatDateForMexico = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Si la fecha viene como string ISO (ej: "2025-06-12T00:00:00.000Z")
      if (dateString.includes('T')) {
        const [datePart] = dateString.split('T');
        const [year, month, day] = datePart.split('-');
        
        // Crear fecha usando UTC para evitar problemas de zona horaria
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
        
        return date.toLocaleDateString('es-MX', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } 
      // Si la fecha viene como string de la BD (ej: "2025-06-12" o "2025-06-12 00:00:00")
      else {
        const dateOnly = dateString.split(' ')[0]; // Tomar solo la parte de la fecha
        const [year, month, day] = dateOnly.split('-');
        
        // Usar Date.UTC para crear la fecha en UTC y evitar problemas de zona horaria
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
        
        return date.toLocaleDateString('es-MX', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
    } catch (error) {
      console.error('Error al formatear fecha:', error, dateString);
      return dateString; // Devolver la fecha original en caso de error
    }
  };

  // Función para formatear fecha corta
  const formatShortDateForMexico = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Si la fecha viene como string ISO (ej: "2025-06-12T00:00:00.000Z")
      if (dateString.includes('T')) {
        const [datePart] = dateString.split('T');
        const [year, month, day] = datePart.split('-');
        
        // Crear fecha usando UTC para evitar problemas de zona horaria
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
        
        return date.toLocaleDateString('es-MX');
      }
      // Si la fecha viene como string de la BD (ej: "2025-06-12" o "2025-06-12 00:00:00")
      else {
        const dateOnly = dateString.split(' ')[0];
        const [year, month, day] = dateOnly.split('-');
        
        // Usar Date.UTC para crear la fecha en UTC y evitar problemas de zona horaria
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
        
        return date.toLocaleDateString('es-MX');
      }
    } catch (error) {
      console.error('Error al formatear fecha corta:', error, dateString);
      return dateString; // Devolver la fecha original en caso de error
    }
  };

  const generateExecutiveSummary = () => {
    const formattedDate = formatDateForMexico(meeting.date);
    const clientNameUpper = meeting.clientName ? meeting.clientName.toUpperCase() : 'CLIENTE DESCONOCIDO';

    return `MINUTA EJECUTIVA - ${clientNameUpper}\n` +
           `Fecha: ${formattedDate}\n\n` +
           `___________________________________________\n\n` +
           `DATOS PRINCIPALES:\n` +
           `• Contactos generados: ${meeting.contactsGenerated}\n` +
           `• Pacientes concretados: ${meeting.patients}\n` +
           `• Tasa de conversión: ${calculateConversionRate()}\n` +
           (meeting.nextMeetingDate ? `• Próxima junta de seguimiento: ${formatDateForMexico(meeting.nextMeetingDate)}\n\n` : '\n') +
           `RESUMEN EJECUTIVO:\n${meeting.observations}\n\n` +
           `ACCIONES:\n${meeting.agreements || 'No se registraron acciones específicas'}\n\n` +
           `___________________________________________\n` +
           `Generado el ${new Date().toLocaleDateString('es-MX')}`;
  };

  const generateQuickMinuta = () => {
    const nextMeetingDate = meeting.nextMeetingDate ? formatShortDateForMexico(meeting.nextMeetingDate) : 'Pendiente';
    const clientName = meeting.clientName || 'Cliente Desconocido';

    return `Fecha de la junta: ${formatShortDateForMexico(meeting.date)}\n` +
           `Cliente: ${clientName}\n\n` +
           `Resumen ejecutivo\n` +
           `• Contactos generados: ${meeting.contactsGenerated}\n` +
           `• Pacientes concretados: ${meeting.patients}\n` +
           `• Observaciones: ${meeting.observations}\n\n` +
           `Acciones acordadas\n${meeting.agreements || 'No se registraron acciones específicas'}\n\n` +
           `Próxima junta de seguimiento: ${nextMeetingDate}`;
  };

  const executiveSummary = generateExecutiveSummary();
  const quickMinuta = generateQuickMinuta();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-center mb-4">
            <img 
              src="https://4tsix0yujj.ufs.sh/f/2vMRHqOYUHc0ruupdRNQiYtyo2GFJjxB80HOpNwcWZhvnAD4" 
              alt="Logo" 
              className="h-16 mb-4"
            />
          </div>
          
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-[#0061A8]">Minuta Ejecutiva</h2>
            <button 
              onClick={onClose}
              className="text-[#63666A] hover:text-[#0061A8]"
            >
              ✕
            </button>
          </div>

          <div className="bg-[#D1D3D4] p-4 rounded-md mb-4">
            <pre className="whitespace-pre-wrap font-sans text-[#63666A]">{executiveSummary}</pre>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => {
                navigator.clipboard.writeText(executiveSummary);
                alert("Minuta ejecutiva copiada al portapapeles");
              }}
              className="bg-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
            >
              Copiar Minuta Ejecutiva
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(quickMinuta);
                alert("Minuta rápida copiada al portapapeles");
              }}
              className="bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Generar Minuta Rápida
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingSummaryModal;
